import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToOne,
} from 'typeorm';
import { AutomationStepExecutionEntity } from './automation-step-execution.entity';
import { BaseEntity } from './base.entity';
import { ConversationSessionEntity } from './conversation-session.entity';
import { UserEntity } from './user.entity';

@Entity('_conversation_call')
@Index(['callSid'], { unique: true })
@Index(['callStatus'])
@Index(['startedAt'])
@Index(['entityType', 'entityId'])
@Index('idx_conversation_call_fallback_for', ['fkUserIdFallbackFor'], {})
export class ConversationCallEntity extends BaseEntity {
	@Column({
		name: 'call_sid',
		type: 'varchar',
		length: 100,
		unique: true,
		nullable: true,
	})
	public callSid?: string;

	@Column({
		name: 'call_provider',
		type: 'varchar',
		length: 50,
		comment: 'Provider used for the call (twilio, vonage, etc.)',
	})
	public callProvider!: string;

	@Column({
		name: 'call_status',
		type: 'varchar',
		length: 50,
		default: "'initiated'",
		comment:
			'Status: initiated, ringing, in-progress, completed, failed, busy, no-answer',
	})
	public callStatus!: string;

	@Column({
		name: 'call_direction',
		type: 'varchar',
		length: 20,
		comment: 'inbound or outbound',
	})
	public callDirection!: 'inbound' | 'outbound';

	@Column({
		name: 'phone_from',
		type: 'varchar',
		length: 50,
		comment: 'Caller phone number',
	})
	public phoneFrom!: string;

	@Column({
		name: 'phone_to',
		type: 'varchar',
		length: 50,
		comment: 'Recipient phone number',
	})
	public phoneTo!: string;

	@Column({
		name: 'fk__automation_step_execution__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public automationStepExecutionId?: number;

	@ManyToOne(() => AutomationStepExecutionEntity, { nullable: true })
	@JoinColumn({
		name: 'fk__automation_step_execution__id',
		referencedColumnName: 'id',
	})
	public automationStepExecution?: AutomationStepExecutionEntity;

	@Column({
		name: '_entity_type',
		type: 'varchar',
		length: 50,
		nullable: true,
		comment: 'Entity type that was called (e.g., client)',
	})
	public entityType?: string;

	@Column({
		name: '_entity_id',
		type: 'varchar',
		length: 255,
		nullable: true,
		comment: 'Entity ID that was called',
	})
	public entityId?: string;

	@Column({
		name: 'started_at',
		type: 'timestamp',
		nullable: true,
		comment: 'When the call was initiated',
	})
	public startedAt?: Date;

	@Column({
		name: 'answered_at',
		type: 'timestamp',
		nullable: true,
		comment: 'When the call was answered',
	})
	public answeredAt?: Date;

	@Column({
		name: 'ended_at',
		type: 'timestamp',
		nullable: true,
		comment: 'When the call ended',
	})
	public endedAt?: Date;

	@Column({
		name: 'duration_seconds',
		type: 'int',
		nullable: true,
		comment: 'Call duration in seconds',
	})
	public durationSeconds?: number;

	@Column({
		name: 'metadata',
		type: 'json',
		nullable: true,
		comment: 'Provider-specific data and additional metadata',
	})
	public metadata?: any;

	/**
	 * The tenant user (`user._id`) this AI-handled call was originally
	 * destined for. Populated by the inbound routing path when an AI
	 * agent picks up a call that targeted a specific rep (their direct
	 * DID, their group's DID, or their RBAC role's DID) and the rep
	 * was unavailable / declined / didn't answer.
	 *
	 * Drives two consumer surfaces:
	 *   - The rep's personal Communications Inbox surfaces these AI
	 *     sessions so the rep can review what the agent handled on
	 *     their behalf while they were out.
	 *   - The Team Communications Overview report counts these toward
	 *     the rep's `aiSessionCount` so a manager can see "how much AI
	 *     covered for this rep this week."
	 *
	 * `null` when the call wasn't intended for any specific rep (e.g.
	 * customer dialed a fully-AI line, or an outbound AI-initiated
	 * call). NEVER backfilled — historical AI calls before this column
	 * existed remain unattributed.
	 */
	@Column('int', {
		name: 'fk_user__id_fallbackFor',
		nullable: true,
		unsigned: true,
	})
	public fkUserIdFallbackFor!: number | null;

	@ManyToOne(() => UserEntity, {
		onDelete: 'SET NULL',
		onUpdate: 'RESTRICT',
		nullable: true,
	})
	@JoinColumn([{ name: 'fk_user__id_fallbackFor', referencedColumnName: 'id' }])
	public fallbackForUser!: UserEntity | null;

	// Reverse relation from ConversationSession
	@OneToOne(() => ConversationSessionEntity, (session) => session.call)
	public session?: ConversationSessionEntity;
}
